/**
 * @nanokart/tryon-studio-sdk
 * Header component - Premium design with centered branding
 */

import React from 'react';
import { useTryOnConfig } from '../../core/TryOnProvider';
import { useSession } from '../../core/hooks/useSession';
import { useCart } from '../../core/hooks/useCart';

export interface HeaderProps {
  className?: string;
  style?: React.CSSProperties;
  showCartBadge?: boolean;
  showEndSession?: boolean;
  onEndSession?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  className,
  style,
  showCartBadge = true,
  showEndSession = false,
  onEndSession,
}) => {
  const { config, features } = useTryOnConfig();
  const { stats } = useSession();
  const { hardCart, totalItems, toggleCart } = useCart();

  return (
    <header
      className={className}
      style={{
        position: 'sticky', top: 0, zIndex: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 14px',
        borderBottom: '1px solid #f1f5f9',
        backgroundColor: '#ffffff',
        flexShrink: 0,
        ...style,
      }}
      data-testid="studio-header"
    >
      {/* Left spacer */}
      <div style={{ width: 40, flexShrink: 0 }} />

      {/* Center: Partner Logo & Name */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          flex: 1, justifyContent: 'center',
        }}
      >
        {config.brandingConfig?.logoUrl && (
          <img
            src={config.brandingConfig.logoUrl}
            alt={config.brandingConfig?.storeName || 'Store'}
            style={{ height: '36px', width: 'auto', objectFit: 'contain', borderRadius: '4px' }}
            data-testid="header-logo"
          />
        )}
        {config.brandingConfig?.storeName && (
          <span
            style={{
              fontWeight: 700,
              fontSize: '15px',
              color: '#1e293b',
              letterSpacing: '-0.01em',
            }}
            data-testid="header-store-name"
          >
            {config.brandingConfig.storeName}
          </span>
        )}
      </div>

      {/* Right: Stats pills */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        {/* Try-ons used indicator */}
        {stats.tryonsGenerated > 0 && (
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              fontSize: '12px', color: '#475569',
              backgroundColor: '#f1f5f9',
              padding: '5px 10px', borderRadius: '9999px',
              fontWeight: 500,
            }}
            data-testid="tryons-generated"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5">
              <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
            </svg>
            <span>{stats.tryonsGenerated}</span>
          </div>
        )}

        {/* Cart badge */}
        {showCartBadge && features.cart && (
          <button
            onClick={() => toggleCart()}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              fontSize: '12px', color: '#2563eb',
              backgroundColor: '#eff6ff',
              padding: '5px 10px', borderRadius: '9999px',
              border: 'none', cursor: 'pointer', fontWeight: 500,
              position: 'relative',
            }}
            data-testid="cart-button"
            aria-label={`Cart with ${totalItems} items`}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 0 1-8 0" />
            </svg>
            <span data-testid="cart-badge">{totalItems}</span>
          </button>
        )}

        {/* End session button (hidden by default for partner context) */}
        {showEndSession && onEndSession && (
          <button
            onClick={onEndSession}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              fontSize: '12px', color: '#dc2626',
              backgroundColor: '#fee2e2',
              padding: '5px 10px', borderRadius: '9999px',
              border: 'none', cursor: 'pointer', fontWeight: 500,
            }}
            data-testid="end-session-button"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16,17 21,12 16,7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            End
          </button>
        )}
      </div>
    </header>
  );
};

export default Header;
